import axios, { AxiosProgressEvent } from "axios";
import * as client from "../client";
import { TemplateUseCase } from "../pages/specialty-assistant/template/data";
import { ROUTES } from "../routes";
import { buildChannel } from "../util";
import * as Token from "./auth_token";
export * as apiTypes from "../client/types.gen";
import * as config from "../config";

// RMS (v25), RMS 22, and ICM YLT all run a chunk/peril pipeline behind a
// terminal collator, so resume / re-run-financials are valid for these cat
// types. The generic /cat/{report_id}/{resume,rerun-financial} endpoints
// branch on this type internally. Derived from the generated client so the
// union tracks the backend's PipelineCatTypeParam literal automatically.
export type EltPipelineType =
  client.ResumeCatPipelineCatReportIdResumePostData["type"];

const baseUrl = () => {
  const channel = buildChannel();
  switch (channel) {
    case "prod":
      return config.BASE_URL_PROD;
    case "dev":
      return config.BASE_URL_DEV;
    case "dev-vm":
      return config.BASE_URL_DEV_VM;
  }
};

const fetchBinary = async (options: {
  method: "get" | "post";
  path: string;
  body?: unknown;
  contentType?: string;
}): Promise<{ data: Blob; filename?: string }> => {
  const url = `${baseUrl()}${options.path}`;
  const config = {
    responseType: "arraybuffer" as const,
    headers: { Authorization: `Bearer ${Token.getToken()}` },
  };
  const response =
    options.method === "get"
      ? await axios.get(url, config)
      : await axios.post(url, options.body, config);
  const headers = response.headers;
  const getHeader = (name: string): string | undefined => {
    if (typeof headers.get === "function") {
      const value = headers.get(name);
      if (typeof value === "string") return value;
    }
    return undefined;
  };
  const disposition = getHeader("Content-Disposition");
  const blob = new Blob([response.data as ArrayBuffer], {
    type:
      options.contentType ??
      getHeader("Content-Type") ??
      "application/octet-stream",
  });
  return {
    data: blob,
    filename: disposition
      ? /filename="([^"]*)"/.exec(disposition)?.[1]
      : undefined,
  };
};

client.OpenAPI.BASE = baseUrl();
client.OpenAPI.interceptors.request.use((request) => {
  const token = Token.getToken();
  if (token) {
    if (request.headers === undefined) {
      request.headers = {};
    }
    request.headers["Authorization"] = `Bearer ${token}`;
  }

  return request;
});

client.OpenAPI.interceptors.response.use((response) => {
  if (response.status === 401 && !response.config.url?.endsWith("login")) {
    Token.logOut();
    window.location.href = ROUTES.LANDING;
  }
  return response;
});

let URL_PROGRESS_CALLBACKS: [string, (e: AxiosProgressEvent) => void][] = [];

client.OpenAPI.interceptors.request.use((request) => {
  request.onUploadProgress = (e: AxiosProgressEvent) => {
    for (const [url, cb] of URL_PROGRESS_CALLBACKS) {
      if (request.url?.startsWith(url)) {
        cb(e);
      }
    }
  };
  return request;
});

export const login = async (
  username: string,
  password: string
): Promise<
  | { success: true; clientId: string; user: client.User }
  | { success: false; failureReason: string }
> => {
  return await client
    .loginLoginPost({ formData: { username, password } })
    .then((r) => {
      Token.setToken(r.access_token);
      return {
        success: true as const,
        clientId: r.client_id,
        user: r.user,
      };
    })
    .catch((rawE: client.ApiError) => {
      if (rawE.status === 422) {
        const e = rawE as client.HttpValidationError;
        console.error("login failed: ", e.detail);
      }
      return { success: false, failureReason: "unknown" };
    });
};

export const logAmplitudeEventRemotely = async (
  eventName: string,
  eventProperties: {
    [key: string]: string | number;
  }
) => {
  return client.logEventLogEventPost({
    requestBody: { event_name: eventName, event_properties: eventProperties },
  });
};

export type LOB =
  client.AsgardListReportsPostAsgardListReportsPostData["requestBody"]["lobs"][number];
export const listReports = async (
  lobs: LOB[]
): Promise<client.ReportMetadataResponse[]> => {
  return client
    .asgardListReportsPostAsgardListReportsPost({ requestBody: { lobs } })
    .then((response) => response.reports);
};

export const getSubmissionAnalytics =
  async (): Promise<client.SubmissionAnalyticsResponse> => {
    return client.getSubmissionAnalyticsAsgardSubmissionAnalyticsGet();
  };

export const listReport = async (
  reportId: string,
  includeAddresses?: boolean
) => {
  return client.asgardListReportAsgardListReportReportIdGet({
    reportId,
    includeAddresses,
  });
};

export const loadAddresses = async (
  reportId: string,
  sovId: string
): Promise<{ addresses: client.Address[] }> => {
  return await client.asgardLoadAddressesAsgardLoadAddressesReportIdSovIdGet({
    reportId,
    sovId,
  });
};

export const fetchEmailThreads = async (reportId: string) => {
  return client.asgardEmailThreadsAsgardEmailThreadsReportIdGet({ reportId });
};

export const uploadEmailThread = async (
  reportId: string,
  files: File[]
): Promise<client.UploadEmailThreadResponse> => {
  return client.asgardUploadEmailThreadAsgardUploadEmailThreadPost({
    reportId,
    formData: { files },
  });
};

export const deleteEmailThread = async (
  reportId: string,
  conversationId: string
): Promise<string[]> => {
  return (
    await client.asgardDeleteEmailThreadAsgardDeleteEmailThreadReportIdPost({
      reportId,
      requestBody: { conversation_id: conversationId },
    })
  ).deleted_files;
};

export type UpdateReport = {
  assignee?: client.UpdateAsignee;
  status?: client.UpdateStatus;
  additionalAssignees?: client.AdditionalAssigneeInfo;
  sovs?: client.Sov[];
  lossRunInfo?: client.LossRunInfo;
  reviewQuestionResponse?: client.ReviewQuestionResponse;
  companyInfo?: client.CompanyInfo;
  attachmentInfo?: client.AttachmentInfo;
  referralText?: client.ReferralText;
  archiveAt?: boolean;
  rerunReport?: boolean;
  rerunComponents?: client.RerunComponent[];
  brokerContact?: client.UpdateBrokerContact;
  limitAndSublimitInfo?: client.LimitAndSublimitInfo;
  policyInfo?: client.PolicyInfo;
  sublimitInfo?: client.SublimitInfo;
  deductibleInfo?: client.AdvancedDeductibleInfo;
  advancedLimitInfo?: client.AdvancedLimitInfo;
  layerInfo?: client.Layer[];
  googleGroupEmail?: client.UpdateGoogleGroup;
  submissionRecipients?: string[];
};
export const updateReport = async (
  reportId: string,
  updates: UpdateReport
): Promise<client.UpdateReportResponse> => {
  return await client.asgardUpdateReportAsgardUpdateReportPost({
    requestBody: {
      report_id: reportId,
      update_assignee: updates.assignee ?? null,
      update_status: updates.status ?? null,
      update_additional_assignees: updates.additionalAssignees ?? null,
      // Addresses are never mutated via this API (They are dropped on the backend)
      // However, these represent a lot of data, so don't send them over the wire.
      update_sovs: updates.sovs
        ? updates.sovs.map((sov) => ({ ...sov, addresses: [] }))
        : null,
      update_loss_runs_info: updates.lossRunInfo ?? null,
      update_review_question_response: updates.reviewQuestionResponse ?? null,
      update_company_info: updates.companyInfo ?? null,
      update_attachment_info: updates.attachmentInfo ?? null,
      rerun_report: updates.rerunReport ?? null,
      rerun_components: updates.rerunComponents ?? null,
      update_referral_text: updates.referralText ?? null,
      update_broker_contact: updates.brokerContact ?? null,
      update_limit_info: updates.limitAndSublimitInfo ?? null,
      update_policy_info: updates.policyInfo ?? null,
      update_sublimit_info: updates.sublimitInfo ?? null,
      update_deductible_info: updates.deductibleInfo ?? null,
      update_advanced_limit_info: updates.advancedLimitInfo ?? null,
      update_layer_info: updates.layerInfo ?? null,
      update_google_group_email: updates.googleGroupEmail ?? null,
      update_submission_recipients: updates.submissionRecipients ?? null,
    },
  });
};

export type UpdateAddresses = {
  addresses: client.Address[];
};
export const updateAddresses = async (
  report_id: string,
  update: UpdateAddresses
): Promise<client.UpdateAddressResponse> => {
  return await client.asgardUpdateAddressAsgardUpdateAddressPost({
    requestBody: { report_id, addresses: update.addresses },
  });
};

export const addManualAddress = async (
  report_id: string,
  address: client.RawAddress
): Promise<client.AddManualAddressResponse> => {
  return await client.asgardAddManualAddressAsgardAddManualAddressPost({
    requestBody: { report_id, address: address },
  });
};

export const downloadLossRun = async (
  parsedLossRuns: client.UserGeneratedLossRunByYear[]
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_loss_run",
    body: { loss_run_years: parsedLossRuns },
  });
  return data;
};

export const downloadPdfZip = async (folderName: string) => {
  const { data } = await fetchBinary({
    method: "get",
    path: `/adonis/download_pdfs?folder_name=${folderName}`,
  });
  return data;
};

export const listUsers = async () => {
  return await client.listUsersListUsersPost();
};

export const listCRMContacts = async () => {
  return await client.listContactsEndpointCrmContactsGet();
};

export const createCRMContact = async (
  requestBody: client.CreateContactRequest
): Promise<client.CreateContactResponse> => {
  return await client.createContactEndpointCrmContactPost({ requestBody });
};

export const updateCRMContact = async (
  contactId: string,
  requestBody: client.UpdateContactRequest
): Promise<client.UpdateContactResponse> => {
  return await client.updateContactEndpointCrmContactContactIdPost({
    contactId,
    requestBody,
  });
};

export const listAdditionalFiles = async (
  reportId: string
): Promise<string[]> => {
  return (
    await client.asgardListAdditionalFilesAsgardListAdditionalFilesReportIdPost(
      { reportId }
    )
  ).files;
};

export const uploadAdditionalFile = async (
  reportId: string,
  files: File[],
  onUploadProgress: (e: AxiosProgressEvent) => void,
  isScreenshot: boolean
): Promise<string[]> => {
  const callback = (e: AxiosProgressEvent) => onUploadProgress(e);
  URL_PROGRESS_CALLBACKS.push([
    `${client.OpenAPI.BASE}/asgard/upload_additional_file`,
    callback,
  ]);
  try {
    return (
      await client.asgardUploadAdditionalFileAsgardUploadAdditionalFilesPost({
        reportId: reportId,
        classificationForAllFiles: isScreenshot ? "screenshot" : null,
        sourceForAllFiles: isScreenshot ? "screenshot_tool" : "user_upload",
        formData: {
          files,
        },
      })
    ).files;
  } finally {
    URL_PROGRESS_CALLBACKS = URL_PROGRESS_CALLBACKS.filter(
      ([, cb]) => cb != callback
    );
  }
};

export const deleteAttachment = async (
  reportId: string,
  filename: string
): Promise<client.AttachmentInfo> => {
  return await client.asgardDeleteAttachmentAsgardDeleteAttachmentPost({
    requestBody: { report_id: reportId, filename },
  });
};

export const deleteAdditionalFile = async (
  reportId: string,
  files: string[]
): Promise<string[]> => {
  return (
    await client.asgardDeleteAdditionalFilesAsgardDeleteAdditionalFilesReportIdPost(
      { reportId, requestBody: { files } }
    )
  ).files;
};

export const testDeductibleOrLimitFunction = async (
  reportId: string,
  filename: string | null,
  perilType: client.peril_type,
  functionType: client.function_type
) => {
  return await client.asgardTestPythonFunctionAsgardTestPythonFunctionReportIdPost(
    {
      reportId,
      requestBody: {
        peril_type: perilType,
        extracted_filename: filename,
        function_type: functionType,
      },
    }
  );
};

export const downloadAdditionalFile = async (
  reportId: string,
  file: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_additional_file",
    body: { report_id: reportId, file },
  });
  return data;
};

export const downloadThreadAttachment = async (
  reportId: string,
  threadUrl: string,
  messageId: string,
  attachmentId: string,
  filename: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_thread_attachment",
    body: {
      report_id: reportId,
      thread_url: threadUrl,
      message_id: messageId,
      attachment_id: attachmentId,
      filename,
    },
  });
  return data;
};

export const generateBulkEdit = async (
  reportId: string,
  field: string,
  instructions: string
): Promise<[client.Address[], client.Address[]]> => {
  const response = await client.generateBulkEditAsgardGenerateBulkEditPost({
    requestBody: { report_id: reportId, field, instructions },
  });
  return [response.before, response.after];
};

export const downloadReportFile = async (
  reportId: string,
  filename: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_report_file",
    body: { report_id: reportId, filename: filename },
  });
  return data;
};

export const loadAddressesForMap = async (lobs: LOB[]) => {
  return await client.asgardLoadAddressesForMapAsgardLoadAddressesForMapPost({
    requestBody: { lobs },
  });
};

export const getBatchComments = async (reportIds: string[]) => {
  return client.asgardBatchCommentsAsgardCommentsPost({
    requestBody: { report_ids: reportIds },
  });
};

export const addComment = async (reportId: string, message: string) => {
  return await client.asgardAddCommentAsgardAddCommentPost({
    requestBody: { report_id: reportId, message },
  });
};

export const deleteComment = async (reportId: string, commentId: string) => {
  return await client.asgardDeleteCommentAsgardDeleteCommentPost({
    requestBody: { report_id: reportId, comment_id: commentId },
  });
};

export const editComment = async (
  reportId: string,
  commentId: string,
  message: string
) => {
  return await client.asgardEditCommentAsgardEditCommentPost({
    requestBody: { report_id: reportId, comment_id: commentId, message },
  });
};

export const generateUnderwritingPdf = async (
  report_id: string,
  html_content: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/generate_underwriting_pdf",
    body: { report_id, html_content },
    contentType: "application/pdf",
  });
  return data;
};

export const quoteIndicationPreflight = async (
  reportId: string,
  layerId: string,
  effectivePerils: client.QuoteIndicationPreflightRequest["effective_perils"],
  perilsInsured: string
) => {
  return client.asgardQuoteIndicationPreflightAsgardQuoteIndicationPreflightPost(
    {
      requestBody: {
        report_id: reportId,
        layer_id: layerId,
        effective_perils: effectivePerils,
        perils_insured: perilsInsured,
      },
    }
  );
};

export const generateQuoteIndicationDraft = async (
  reportId: string,
  layerId: string,
  effectivePerils: client.GenerateQuoteIndicationRequest["effective_perils"],
  perilsInsured: string,
  premiumRange: string
) => {
  return client.asgardGenerateQuoteIndicationDraftAsgardGenerateQuoteIndicationDraftPost(
    {
      requestBody: {
        report_id: reportId,
        layer_id: layerId,
        effective_perils: effectivePerils,
        perils_insured: perilsInsured,
        premium_range: premiumRange,
      },
    }
  );
};

export const downloadUawSheet = async (reportId: string) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_uaw",
    body: { report_id: reportId },
  });
  return data;
};

export const deleteReport = async (reportId: string) => {
  return await client.asgardDeleteReportAsgardDeleteReportPost({
    requestBody: { report_id: reportId },
  });
};

export const enqueueRater = async (
  reportId: string,
  templateId: string | undefined
) => {
  return client.asgardRateSubmissionAsgardRateSubmissionPost({
    requestBody: { report_id: reportId, template_override_id: templateId },
  });
};

export const ratePrecheck = async (
  reportId: string
): Promise<client.RatePrecheckResponse> => {
  return client.asgardRatePrecheckAsgardRatePrecheckReportIdGet({ reportId });
};

export const listReportJobs = async (
  reportIds: string[],
  statuses: client.WorkStatus[] = [
    "pending",
    "in_progress",
    "done",
    "failed",
    "cancelled",
  ]
) => {
  const response = await client.asgardListReportJobsAsgardListReportJobsPost({
    requestBody: {
      report_ids: reportIds,
      statuses,
    },
  });
  return response.jobs;
};

export const downloadRaterWorkbook = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/rater/download/${reportId}/${batchId}`,
    contentType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const fetchRateComponents = async (
  reportId: string,
  batchId: string
): Promise<client.RateComponentsResponse> => {
  return client.getRateComponentsRaterRateComponentsReportIdBatchIdGet({
    reportId,
    batchId,
  });
};

export const previewEml = async (
  reportId: string
): Promise<{ subject: string; eml: string }> => {
  return await client.asgardEmlPreviewAsgardEmlPreviewPost({
    requestBody: { report_id: reportId },
  });
};

export const downloadReportHistory = async (
  reportIds: string[],
  downloadType: client.download_type
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_report_history",
    body: { report_ids: reportIds, download_type: downloadType },
  });
  return data;
};

export const getUpdatingReports = async (since: Date) => {
  return await client.asgardListUpdatingReportsAsgardListUpdatingReportsPost({
    requestBody: { since: since.toISOString() },
  });
};

export const getBrokerChartData = async (
  request: client.BrokerAnalyticsCrmBrokerAnalyticsGetData
) => {
  return await client.brokerAnalyticsCrmBrokerAnalyticsGet(request);
};

export const getSubmissionStatusCounts = async () => {
  return await client.getSubmissionStatusCountsCrmSubmissionStatusCountsGet();
};

export const listPolicyFlyApplications = async (
  statuses: string
): Promise<client.PolicyFlyApplicationRow[]> => {
  return client
    .getPolicyflyApplicationsCrmPolicyflyApplicationsGet({ statuses })
    .then((response) => response.applications);
};

export const exportBrokerData = async () => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/crm/broker_export",
    body: {},
  });
  return data;
};

export const listTemplates = async () => {
  return await client.asgardListTemplatesAsgardListTemplatesPost();
};

export const createTemplate = async (
  template: File,
  useCase: TemplateUseCase
) => {
  return await client.asgardCreateTemplateAsgardCreateTemplatePost({
    formData: { template },
    useCase,
  });
};

export const deleteTemplate = async (requestBody: client.DeleteTemplate) => {
  return await client.asgardDeleteTemplateAsgardDeleteTemplatePost({
    requestBody,
  });
};
export const updateTemplate = async (requestBody: client.UpdateTemplate) => {
  return await client.asgardUpdateTemplateAsgardUpdateTemplatePost({
    requestBody,
  });
};

export const downloadTemplate = async (template: client.Template) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_template",
    body: { template_id: template.template_id },
  });
  return data;
};

export const generatePolicyNumbers = async (reportId: string) => {
  return await client.asgardGeneratePolicyNumbersAsgardGeneratePolicyNumbersPost(
    { requestBody: { report_id: reportId } }
  );
};

export const generatePolicyFlyApplication = async (
  reportId: string,
  perilsByLayer: Record<string, string>,
  additionalLayerId?: string
) => {
  return await client.asgardGeneratePolicyFlyApplicationAsgardGeneratePolicyFlyApplicationPost(
    {
      requestBody: {
        report_id: reportId,
        perils_by_layer: perilsByLayer,
        additional_layer_id: additionalLayerId ?? null,
      },
    }
  );
};

export const initiateIcmRequest = async (
  reportId: string
): Promise<client.InitiateRequestEndpointIcmInitiatePostResponse> => {
  return client.initiateRequestEndpointIcmInitiatePost({
    requestBody: { report_id: reportId },
  });
};

export const testIcmRequest = async (
  reportId: string,
  modelMode: client.InitiateTestRequest["model_mode"],
  airModelId: client.InitiateTestRequest["air_model_id"],
  layerIds: string[],
  perilCodes: client.InitiateTestRequest["peril_codes"],
  elevationOverride?: string | null
): Promise<client.InitiateTestRequestEndpointIcmInitiateTestPostResponse> => {
  return client.initiateTestRequestEndpointIcmInitiateTestPost({
    requestBody: {
      report_id: reportId,
      model_mode: modelMode,
      air_model_id: airModelId,
      peril_codes: perilCodes,
      layer_ids: layerIds,
      elevation_override: elevationOverride,
    },
  });
};

export const icmRequestStatus = async (
  requestId: string
): Promise<client.RequestStatusIcmStatusRequestIdGetResponse> => {
  return client.requestStatusIcmStatusRequestIdGet({ requestId });
};

// Unified CAT results endpoint
export const catResults = async (
  reportId: string,
  batchId?: string
): Promise<client.CatResultsResponse> => {
  return client.getCatResultsCatResultsReportIdGet({
    reportId,
    batchId: batchId ?? null,
  });
};

export const catPrecheck = async (
  reportId: string
): Promise<client.CatPrecheckResponse> => {
  return client.catPrecheckCatPrecheckReportIdGet({ reportId });
};

export const icmJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistoryCatJobHistoryReportIdGet({
    reportId,
    type: "icm",
  });
};

export const downloadIcmArtifact = async (
  reportId: string,
  batchId: string,
  artifact: "analyses" | "request" | "error_response"
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download/${reportId}/${batchId}?type=icm&artifact=${artifact}`,
    contentType: "application/xml",
  });
};

export const downloadAllIcmArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=icm`,
    contentType: "application/zip",
  });
};

export const sendClearanceEmail = async (
  reportId: string,
  status: client.status
) => {
  return client.asgardSendClearanceEmailAsgardSendClearanceEmailPost({
    requestBody: {
      report_id: reportId,
      status: status,
    },
  });
};

// RMS API functions — use generated client where possible
export const initiateRmsRequest = async (
  reportId: string,
  maxLocationOverride: boolean
): Promise<client.RmsInitiateResponse> => {
  return client.initiateRmsRequestRmsInitiatePost({
    requestBody: {
      report_id: reportId,
      layer_ids: null,
      max_location_override: maxLocationOverride,
    },
  });
};

export const rmsRequestStatus = async (
  requestId: string
): Promise<client.IntegrationStatusResult> => {
  return client.rmsRequestStatusRmsStatusRequestIdGet({ requestId });
};

export const rmsJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistoryCatJobHistoryReportIdGet({
    reportId,
    type: "rms",
  });
};

export const downloadRmsArtifact = async (
  reportId: string,
  batchId: string,
  artifact: "analyses" | "request" | "error_response"
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download/${reportId}/${batchId}?type=rms&artifact=${artifact}`,
    contentType: "application/json",
  });
};

export const downloadAllRmsArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=rms`,
    contentType: "application/zip",
  });
};

// ── RMS ELT ─────────────────────────────────────────────────────────────────

export const initiateRmsEltRequest = async (
  reportId: string
): Promise<client.RmsEltInitiateResponse> => {
  return client.initiateRmsEltRequestRmsEltInitiatePost({
    requestBody: { report_id: reportId, version: "25" },
  });
};

export const rmsEltRequestStatus = async (
  requestId: string
): Promise<client.IntegrationStatusResult> => {
  return client.rmsEltRequestStatusRmsEltStatusRequestIdGet({
    requestId,
    version: "25",
  });
};

export const rmsEltJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistoryCatJobHistoryReportIdGet({
    reportId,
    type: "rms_elt",
  });
};

export const downloadAllRmsEltArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=rms_elt`,
    contentType: "application/zip",
  });
};

export const rerunRmsEltFinancials = async (
  reportId: string,
  type: EltPipelineType,
  batchId: string
): Promise<client.CatRerunFinancialResult> => {
  return client.rerunCatFinancialCatReportIdRerunFinancialPost({
    reportId,
    type,
    batchId,
  });
};

// ``mode`` is meaningful for icm_ylt only: "resume" keeps each failed
// chunk's ICM status URL (continue polling); "restart" drops it so
// re-dispatched chunks submit fresh ICM ratings — the escape hatch for a
// run stuck on a status URL that never resolves. The backend rejects
// "restart" for the RMS types.
export const resumeRmsEltPipeline = async (
  reportId: string,
  type: EltPipelineType,
  batchId: string,
  mode: "resume" | "restart"
): Promise<client.CatResumeResult> => {
  return client.resumeCatPipelineCatReportIdResumePost({
    reportId,
    type,
    batchId,
    mode,
  });
};

// ── RMS 22 ─────────────────────────────────────────────────────────────────
// Same ELT pipeline as RMS (v25); the version param selects v22.

export const initiateRms22Request = async (
  reportId: string
): Promise<client.RmsEltInitiateResponse> => {
  return client.initiateRmsEltRequestRmsEltInitiatePost({
    requestBody: { report_id: reportId, version: "22" },
  });
};

export const rms22RequestStatus = async (
  requestId: string
): Promise<client.IntegrationStatusResult> => {
  return client.rmsEltRequestStatusRmsEltStatusRequestIdGet({
    requestId,
    version: "22",
  });
};

export const rms22JobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistoryCatJobHistoryReportIdGet({
    reportId,
    type: "rms_22",
  });
};

export const downloadAllRms22Artifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=rms_22`,
    contentType: "application/zip",
  });
};

// ── ICM YLT ─────────────────────────────────────────────────────────────────

export const initiateIcmYltRequest = async (
  reportId: string
): Promise<client.IcmYltInitiateResponse> => {
  return client.initiateIcmYltRequestIcmYltInitiatePost({
    requestBody: { report_id: reportId },
  });
};

export const icmYltRequestStatus = async (
  requestId: string
): Promise<client.IntegrationStatusResult> => {
  return client.icmYltRequestStatusIcmYltStatusRequestIdGet({ requestId });
};

export const icmYltJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistoryCatJobHistoryReportIdGet({
    reportId,
    type: "icm_ylt",
  });
};

export const downloadAllIcmYltArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=icm_ylt`,
    contentType: "application/zip",
  });
};

// ── Nearmap ─────────────────────────────────────────────────────────────────

export const initiateNearmapRequest = async (
  reportId: string
): Promise<client.NearmapInitiateResponse> => {
  return client.initiateNearmapRequestNearmapInitiatePost({
    requestBody: { report_id: reportId },
  });
};

export const nearmapRequestStatus = async (
  requestId: string
): Promise<client.IntegrationStatusResult> => {
  return client.nearmapRequestStatusNearmapStatusRequestIdGet({ requestId });
};

export const nearmapJobHistory = async (
  reportId: string
): Promise<client.NearmapJobHistoryResponse> => {
  return client.getNearmapJobHistoryNearmapJobHistoryReportIdGet({ reportId });
};

// ── SpatialKat (KatRisk) ─────────────────────────────────────────────────────

export const initiateSpatialKatRequest = async (
  reportId: string
): Promise<client.SpatialKatInitiateResponse> => {
  return client.initiateSpatialkatRequestSpatialkatInitiatePost({
    requestBody: { report_id: reportId },
  });
};

export const spatialKatRequestStatus = async (
  requestId: string
): Promise<client.IntegrationStatusResult> => {
  return client.spatialkatRequestStatusSpatialkatStatusRequestIdGet({
    requestId,
  });
};

export const spatialKatJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistoryCatJobHistoryReportIdGet({
    reportId,
    type: "spatialkat",
  });
};

export const downloadAllSpatialKatArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=spatialkat`,
    contentType: "application/zip",
  });
};

export const calculatePremium = async (reportId: string, batchId?: string) => {
  return client.calculatePremiumAsgardPremiumCalculatedPost({
    requestBody: { report_id: reportId, batch_id: batchId },
  });
};

export const getIntegrationData = async (
  reportId: string,
  types: client.IntegrationDataRequest["types"],
  addressIds: string[]
): Promise<client.IntegrationDataResponse> => {
  return client.getIntegrationDataIntegrationDataPost({
    requestBody: { report_id: reportId, types, address_ids: addressIds },
  });
};

export const getDeductibleSummary = async (
  reportId: string,
  addressIds: string[]
): Promise<client.DeductibleSummaryResponse> => {
  return client.getDeductibleSummaryDeductibleSummaryPost({
    requestBody: { report_id: reportId, address_ids: addressIds },
  });
};

export const getLimitSummary = async (
  reportId: string,
  addressIds: string[]
): Promise<client.LimitSummaryResponse> => {
  return client.getLimitSummaryLimitSummaryPost({
    requestBody: { report_id: reportId, address_ids: addressIds },
  });
};

export type WindTier = NonNullable<client.LocationClassification["wind_tier"]>;
export type WindGate = NonNullable<client.LocationClassification["wind_gate"]>;
export type EqGate = NonNullable<client.LocationClassification["eq_gate"]>;
export type LocationKey = client.LocationKey;
export type LocationClassificationKey = string & {
  readonly __brand: "LocationClassificationKey";
};

// Per-location catastrophe gate classifications, keyed off the same surface
// (state, county) strings the caller sent. Each field is null when the
// location doesn't fall into that peril's gate.
export type LocationClassification = {
  windTier: WindTier | null;
  windGate: WindGate | null;
  eqGate: EqGate | null;
};

export const getLocationClassifications = async (
  locations: LocationKey[]
): Promise<client.LocationClassificationsResponse> => {
  return client.getLocationClassificationsLocationClassificationsPost({
    requestBody: { locations },
  });
};

export const getGoogleGroupStats = async (email: string) => {
  return client.asgardGoogleGroupStatsAsgardGoogleGroupStatsPost({
    requestBody: { google_group_email: email },
  });
};

export const getClearanceEmailRecipients = async (
  reportId: string
): Promise<{ recipients: string[]; locked_emails: string[] }> => {
  return client.asgardClearanceEmailRecipientsAsgardClearanceEmailRecipientsPost(
    { requestBody: { report_id: reportId } }
  );
};

export const listReportActionLogs = async (): Promise<
  client.ReportActionSummaryWithName[]
> => {
  return client
    .asgardListReportActionLogsAsgardListReportActionLogsPost({
      requestBody: {},
    })
    .then((response) => response.summaries);
};

export const getReportActionLog = async (
  id: string
): Promise<client.GetReportActionLogResponse> => {
  const isEmail = id.startsWith("email:");
  const requestBody = isEmail
    ? { email_message_id: id.slice("email:".length) }
    : { report_id: id };
  return client.asgardGetReportActionLogAsgardGetReportActionLogPost({
    requestBody,
  });
};

// ── BOR (Broker of Record) ──

export const createBorLetter = async (
  request: client.CreateBorLetterRequest
): Promise<client.BorLetterResponse> => {
  return client.createBorLetterEndpointBorCreatePost({
    requestBody: request,
  });
};

export const getBorLetter = async (
  borId: string
): Promise<client.BorLetterResponse> => {
  return client.getBorLetterEndpointBorGetPost({
    requestBody: { bor_id: borId },
  });
};

export const getBorLettersByReport = async (
  reportId: string
): Promise<client.BorLetterResponse[]> => {
  return client
    .getBorLettersByReportEndpointBorByReportPost({
      requestBody: { report_id: reportId },
    })
    .then((r) => r.bor_letters);
};

export const listBorLetters = async (
  activeOnly: boolean = false
): Promise<client.BorLetterResponse[]> => {
  return client
    .listBorLettersEndpointBorListPost({
      requestBody: { active_only: activeOnly },
    })
    .then((r) => r.bor_letters);
};

export const markBorStep = async (
  borId: string,
  step: client.MarkBorStepRequest["step"]
): Promise<client.BorLetterResponse> => {
  return client.markBorStepEndpointBorMarkStepPost({
    requestBody: { bor_id: borId, step },
  });
};
